import React from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  IconButton,
  Button,
  Chip,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import {
  ChevronDown,
  ChevronUp,
  X,
  UploadCloud,
  RotateCw,
  Check,
} from 'lucide-react';
import { CandiBankUploadItem } from '../hooks/useCandiBankUpload';
import { UsageMeter } from '../../../ui/usage_meter/UsageMeter';

interface CandiBankUploadWidgetProps {
  items: CandiBankUploadItem[];
  isOpen: boolean;
  isMinimized: boolean;
  onMinimizeToggle: () => void;
  onClose: () => void;
  onRetry: (id: string) => void;
  onRemove: (id: string) => void;
  onClearCompleted: () => void;
  formatFileSize: (bytes: number) => string;
  overallProgress: number;
  completedCount: number;
  duplicateCount?: number;
  totalCount: number;
  isUploading: boolean;
}

export const CandiBankUploadWidget: React.FC<CandiBankUploadWidgetProps> = ({
  items,
  isOpen,
  isMinimized,
  onMinimizeToggle,
  onClose,
  onRetry,
  onRemove,
  onClearCompleted,
  formatFileSize,
  overallProgress,
  completedCount,
  duplicateCount = 0,
  totalCount,
  isUploading,
}) => {
  if (!isOpen || totalCount === 0) return null;

  const failedCount = items.filter((i) => i.status === 'failed').length;
  const blockedCount = items.filter((i) => i.status === 'blocked').length;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 20,
        right: 24,
        zIndex: 9990,
        transition: 'all 0.3s ease-in-out',
        width: isMinimized ? 320 : { xs: 'calc(100vw - 32px)', sm: 460 },
      }}
    >
      <Paper
        elevation={6}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'grey.300',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
        }}
      >
        {/* Header */}
        <Box
          onClick={isMinimized ? onMinimizeToggle : undefined}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            bgcolor: 'grey.900',
            color: 'common.white',
            cursor: isMinimized ? 'pointer' : 'default',
            '&:hover': isMinimized ? { bgcolor: 'grey.800' } : {},
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {isUploading ? (
                <CircularProgress size={16} sx={{ color: 'success.light' }} />
              ) : (
                <UploadCloud size={16} color="#34d399" />
              )}
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'common.white', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {isUploading
                  ? `Uploading ${totalCount - completedCount - duplicateCount} of ${totalCount} CVs...`
                  : `Uploaded ${completedCount + duplicateCount} of ${totalCount} CVs`}
              </Typography>

              {isMinimized && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={overallProgress}
                    sx={{
                      width: 96,
                      height: 5,
                      borderRadius: 3,
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      '& .MuiLinearProgress-bar': { bgcolor: 'success.light' },
                    }}
                  />
                  <Typography variant="caption" sx={{ fontSize: '0.625rem', fontFamily: 'monospace', fontWeight: 700, color: 'grey.300' }}>
                    {overallProgress}%
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>

          <Stack direction="row" spacing={0.5} sx={{ ml: 1 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onMinimizeToggle();
              }}
              sx={{ color: 'grey.400', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
              title={isMinimized ? 'Expand' : 'Minimize'}
            >
              {isMinimized ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </IconButton>

            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              sx={{ color: 'grey.400', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
              title="Close"
            >
              <X size={16} />
            </IconButton>
          </Stack>
        </Box>

        {/* Expanded Content Area */}
        {!isMinimized && (
          <Box>
            {/* Overall Progress Bar & Clean SaaS Metric Badges */}
            <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'grey.200' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Upload Progress
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'text.primary' }}>
                  {overallProgress}% ({completedCount + duplicateCount}/{totalCount} Processed)
                </Typography>
              </Box>

              <UsageMeter used={overallProgress} limit={100} tone="ok" />

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 1, mt: 0.5 }}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  <Chip
                    label={`${completedCount} Done`}
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.625rem', fontWeight: 600 }}
                  />

                  {duplicateCount > 0 && (
                    <Chip
                      label={`${duplicateCount} Duplicate`}
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.625rem', fontWeight: 600 }}
                    />
                  )}

                  {isUploading && (
                    <Chip
                      label="Processing..."
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.625rem', fontWeight: 600 }}
                    />
                  )}

                  {failedCount > 0 && (
                    <Chip
                      label={`${failedCount} Failed`}
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.625rem', fontWeight: 600 }}
                    />
                  )}

                  {blockedCount > 0 && (
                    <Chip
                      label={`${blockedCount} No credits`}
                      size="small"
                      color="error"
                      variant="outlined"
                      title="Monthly CV credit limit reached"
                      sx={{ height: 20, fontSize: '0.625rem', fontWeight: 600 }}
                    />
                  )}
                </Stack>

                {(completedCount > 0 || duplicateCount > 0 || blockedCount > 0) && (
                  <Button
                    size="small"
                    onClick={onClearCompleted}
                    sx={{ fontSize: '0.6875rem', fontWeight: 600, color: 'text.secondary', textTransform: 'none', p: 0 }}
                  >
                    Clear completed
                  </Button>
                )}
              </Box>
            </Box>

            {/* Scrollable File List */}
            <Box sx={{ maxHeight: 280, overflowY: 'auto', p: 1, bgcolor: 'background.paper' }}>
              <Stack spacing={0.5}>
                {items.map((item) => (
                  <Paper
                    key={item.id}
                    elevation={0}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.5,
                      p: 1.25,
                      borderRadius: 2,
                      bgcolor: 'grey.50',
                      '&:hover': { bgcolor: 'grey.100' },
                      '&:hover .remove-btn': { opacity: 1 },
                    }}
                  >
                    {/* File Format Badge Icon */}
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        bgcolor: 'grey.200',
                        color: 'text.secondary',
                        fontWeight: 700,
                        fontSize: '0.625rem',
                        textTransform: 'uppercase',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {item.name.split('.').pop()?.substring(0, 4) || 'CV'}
                    </Box>

                    {/* File Info & Status */}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={item.name}
                        >
                          {item.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled', flexShrink: 0, fontSize: '0.625rem' }}>
                          {formatFileSize(item.size)}
                        </Typography>
                      </Box>

                      {item.status === 'duplicate' ? (
                        <Typography variant="caption" sx={{ color: 'warning.dark', display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.625rem' }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'warning.main', display: 'inline-block' }} />
                          {item.error || 'Duplicate CV — Already in Candi Bank'}
                        </Typography>
                      ) : item.status === 'failed' ? (
                        <Typography variant="caption" sx={{ color: 'error.main', fontSize: '0.625rem' }}>
                          {item.error || 'Upload error'}
                        </Typography>
                      ) : item.status === 'blocked' ? (
                        <Typography variant="caption" sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.625rem' }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'error.main', display: 'inline-block' }} />
                          {item.error || 'Monthly CV credit limit reached'}
                        </Typography>
                      ) : (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                          <LinearProgress
                            variant="determinate"
                            value={item.progress}
                            color={item.status === 'completed' ? 'success' : 'primary'}
                            sx={{ flex: 1, height: 5, borderRadius: 2 }}
                          />
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', width: 32, textAlign: 'right', fontSize: '0.625rem' }}>
                            {item.progress}%
                          </Typography>
                        </Stack>
                      )}
                    </Box>

                    {/* Action Badges & Icons */}
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                      {item.status === 'completed' && (
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: 'success.50',
                            color: 'success.main',
                            border: '1px solid',
                            borderColor: 'success.light',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          title="Uploaded"
                        >
                          <Check size={12} />
                        </Box>
                      )}

                      {item.status === 'duplicate' && (
                        <Chip
                          label="Duplicate"
                          size="small"
                          color="warning"
                          sx={{ height: 18, fontSize: '0.625rem' }}
                        />
                      )}

                      {item.status === 'blocked' && (
                        <Chip
                          label="Blocked"
                          size="small"
                          color="error"
                          sx={{ height: 18, fontSize: '0.625rem' }}
                        />
                      )}

                      {(item.status === 'failed' || item.status === 'blocked') && (
                        <IconButton
                          size="small"
                          onClick={() => onRetry(item.id)}
                          sx={{ color: 'text.secondary', '&:hover': { color: 'success.main' } }}
                          title={item.status === 'blocked' ? 'Retry (once credits are available)' : 'Retry'}
                        >
                          <RotateCw size={13} />
                        </IconButton>
                      )}

                      <IconButton
                        size="small"
                        color="error"
                        className="remove-btn"
                        onClick={() => onRemove(item.id)}
                        sx={{
                          opacity: 0,
                          transition: 'opacity 0.2s',
                        }}
                        title="Remove"
                      >
                        <X size={13} />
                      </IconButton>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
